import { useMemo, useState } from 'react';
import { Code, Globe, Hash, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import {
  decodeBase64,
  encodeBase64Text,
  type Base64Variant,
} from '../lib/base64';
import {
  buildQuery,
  decodeUrl,
  encodeUrl,
  splitUrlAndQuery,
  type UrlMode,
} from '../lib/urlCodec';
import { decodeHex, encodeHexText } from '../lib/hex';
import { CodecPanel } from '../components/CodecPanel';
import { ToolHeader } from '../components/ToolHeader';

/**
 * `/tools/encode/codec` — unified Base64 / URL / Hex codec.
 *
 * Three classic encoders share an identical "raw ↔ encoded" loop, so
 * this page replaces the previous three (`/tools/encode/{base64,url,hex}`).
 * The type switcher controls which lib runs; per-type knobs (variant,
 * separator, etc.) render in the options slot.
 *
 * URL keeps its query inspector as an extra slot below the main pane —
 * inspecting `https://...?a=1&b=2` is structurally close enough to URL
 * encoding/decoding that splitting it into a separate page would cost
 * more in navigation than it saves in clarity.
 */

type CodecKind = 'base64' | 'url' | 'hex';
type Direction = 'encode' | 'decode';

const KIND_META: Record<
  CodecKind,
  { name: string; icon: React.ComponentType<{ className?: string }>; placeholder: string }
> = {
  base64: { name: 'Base64', icon: Code, placeholder: '在此粘贴或输入字符串…' },
  url: { name: 'URL', icon: Globe, placeholder: '在此粘贴或输入字符串…' },
  hex: { name: 'Hex', icon: Hash, placeholder: '在此粘贴或输入字符串…' },
};

interface PerKindState {
  input: string;
  direction: Direction;
}

const EMPTY: PerKindState = { input: '', direction: 'encode' };

interface Base64Opts {
  variant: Base64Variant;
  noPadding: boolean;
}
type Separator = 'none' | 'space' | 'colon';
const SEP_TO_STR: Record<Separator, string> = { none: '', space: ' ', colon: ':' };
interface HexOpts {
  uppercase: boolean;
  separator: Separator;
}
interface UrlOpts {
  mode: UrlMode;
}

export default function CodecTool(): JSX.Element {
  const [kind, setKind] = useState<CodecKind>('base64');
  // One state slot per kind so switching tabs preserves whatever the
  // user typed — going back to base64 after a hex round-trip should
  // not lose your input.
  const [perKind, setPerKind] = useState<Record<CodecKind, PerKindState>>({
    base64: { ...EMPTY },
    url: { ...EMPTY },
    hex: { ...EMPTY },
  });

  const [base64Opts, setBase64Opts] = useState<Base64Opts>({
    variant: 'standard',
    noPadding: false,
  });
  const [urlOpts, setUrlOpts] = useState<UrlOpts>({ mode: 'component' });
  const [hexOpts, setHexOpts] = useState<HexOpts>({ uppercase: false, separator: 'none' });

  const state = perKind[kind];
  const setInput = (s: string): void =>
    setPerKind((p) => ({ ...p, [kind]: { ...p[kind], input: s } }));
  const setDirection = (d: Direction): void =>
    setPerKind((p) => ({ ...p, [kind]: { ...p[kind], direction: d } }));

  const result = useMemo(
    () => runCodec(kind, state, base64Opts, urlOpts, hexOpts),
    [kind, state, base64Opts, urlOpts, hexOpts],
  );

  const onSwap = (): void => {
    if (!result.ok || result.output.length === 0) return;
    setPerKind((p) => ({
      ...p,
      [kind]: {
        input: result.output,
        direction: state.direction === 'encode' ? 'decode' : 'encode',
      },
    }));
  };

  const inputLabel = state.direction === 'encode' ? '原文' : KIND_META[kind].name;
  const outputLabel = state.direction === 'encode' ? KIND_META[kind].name : '原文';

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <ToolHeader
        toolId="codec"
        category="encode"
        description="Base64 / URL / Hex 三合一。原文 ↔ 编码内容，所有运算都在浏览器里完成，不会上传任何数据。"
      />

      <div className="card-pad flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-md border border-line">
          {(['base64', 'url', 'hex'] as CodecKind[]).map((k) => {
            const Icon = KIND_META[k].icon;
            const active = kind === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => setKind(k)}
                className={clsx(
                  'inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium transition first:rounded-l-md last:rounded-r-md',
                  active
                    ? 'bg-accent/10 text-accent'
                    : 'text-ink-muted hover:bg-bg-soft hover:text-ink',
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {KIND_META[k].name}
              </button>
            );
          })}
        </div>
        <div className="inline-flex rounded-md border border-line">
          {(['encode', 'decode'] as Direction[]).map((d) => {
            const active = state.direction === d;
            return (
              <button
                key={d}
                type="button"
                onClick={() => setDirection(d)}
                className={clsx(
                  'px-3 py-1 text-xs font-medium transition first:rounded-l-md last:rounded-r-md',
                  active
                    ? 'bg-accent/10 text-accent'
                    : 'text-ink-muted hover:bg-bg-soft hover:text-ink',
                )}
              >
                {d === 'encode' ? '编码' : '解码'}
              </button>
            );
          })}
        </div>
      </div>

      <CodecPanel
        inputLabel={inputLabel}
        outputLabel={outputLabel}
        inputValue={state.input}
        onInputChange={setInput}
        outputValue={result.ok ? result.output : ''}
        outputError={result.ok ? undefined : result.error}
        onSwap={onSwap}
        inputPlaceholder={KIND_META[kind].placeholder}
        optionsSlot={
          kind === 'base64' ? (
            <Base64Options
              opts={base64Opts}
              direction={state.direction}
              onChange={setBase64Opts}
            />
          ) : kind === 'url' ? (
            <UrlOptions opts={urlOpts} onChange={setUrlOpts} />
          ) : (
            <HexOptions opts={hexOpts} direction={state.direction} onChange={setHexOpts} />
          )
        }
        extraSlot={kind === 'url' ? <QueryInspector /> : undefined}
      />
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* Per-kind option strips                                               */
/* -------------------------------------------------------------------- */

function Base64Options({
  opts,
  direction,
  onChange,
}: {
  opts: Base64Opts;
  direction: Direction;
  onChange: (next: Base64Opts) => void;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-ink-muted">
      <label className="flex items-center gap-1">
        变体
        <select
          className="select py-1"
          value={opts.variant}
          onChange={(e) => onChange({ ...opts, variant: e.target.value as Base64Variant })}
        >
          <option value="standard">标准 (+/)</option>
          <option value="urlsafe">URL-safe (-_)</option>
        </select>
      </label>
      {direction === 'encode' && (
        <label className="flex items-center gap-1">
          <input
            type="checkbox"
            className="h-3.5 w-3.5"
            checked={opts.noPadding}
            onChange={(e) => onChange({ ...opts, noPadding: e.target.checked })}
          />
          去除 = padding
        </label>
      )}
    </div>
  );
}

function UrlOptions({
  opts,
  onChange,
}: {
  opts: UrlOpts;
  onChange: (next: UrlOpts) => void;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-ink-muted">
      <label className="flex items-center gap-1">
        模式
        <select
          className="select py-1"
          value={opts.mode}
          onChange={(e) => onChange({ ...opts, mode: e.target.value as UrlMode })}
        >
          <option value="component">component (转义全部)</option>
          <option value="uri">URI (保留结构字符)</option>
        </select>
      </label>
    </div>
  );
}

function HexOptions({
  opts,
  direction,
  onChange,
}: {
  opts: HexOpts;
  direction: Direction;
  onChange: (next: HexOpts) => void;
}): JSX.Element {
  if (direction !== 'encode') {
    return (
      <span className="text-[11px] text-ink-dim">解码端会忽略空白 / 0x 前缀 / 冒号、短横线分隔</span>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-ink-muted">
      <label className="flex items-center gap-1">
        <input
          type="checkbox"
          className="h-3.5 w-3.5"
          checked={opts.uppercase}
          onChange={(e) => onChange({ ...opts, uppercase: e.target.checked })}
        />
        大写
      </label>
      <label className="flex items-center gap-1">
        分隔
        <select
          className="select py-1"
          value={opts.separator}
          onChange={(e) => onChange({ ...opts, separator: e.target.value as Separator })}
        >
          <option value="none">无</option>
          <option value="space">空格</option>
          <option value="colon">冒号</option>
        </select>
      </label>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* URL query inspector — unchanged from the old UrlCodecTool            */
/* -------------------------------------------------------------------- */

interface QueryRow {
  key: string;
  value: string;
}

function QueryInspector(): JSX.Element {
  const [raw, setRaw] = useState('');
  const split = useMemo(() => splitUrlAndQuery(raw), [raw]);
  // Two layers of state: the raw URL and any explicit edits on the
  // query rows. Pasting a fresh URL resets edits; row edits override
  // whatever was parsed.
  const [edits, setEdits] = useState<QueryRow[] | null>(null);

  const rows: QueryRow[] = edits ?? split?.query ?? [];

  const setRowAt = (idx: number, next: QueryRow): void => {
    const base = edits ?? split?.query ?? [];
    const copy = base.slice();
    copy[idx] = next;
    setEdits(copy);
  };
  const deleteRowAt = (idx: number): void => {
    const base = edits ?? split?.query ?? [];
    setEdits(base.filter((_, i) => i !== idx));
  };
  const addRow = (): void => {
    const base = edits ?? split?.query ?? [];
    setEdits([...base, { key: '', value: '' }]);
  };

  const rebuiltQuery = buildQuery(rows);
  const rebuiltUrl = split
    ? `${split.origin}${split.path}${rebuiltQuery ? '?' + rebuiltQuery : ''}${split.hash}`
    : '';

  return (
    <section className="card-pad flex flex-col gap-2">
      <header className="flex items-center gap-2">
        <h2 className="text-sm font-medium text-ink">查询参数</h2>
        <span className="text-[11px] text-ink-dim">粘贴一个完整 URL，下方会拆出 query 并支持编辑</span>
      </header>
      <input
        className="input font-mono text-xs"
        placeholder="https://example.com/api?a=1&b=2#frag"
        value={raw}
        onChange={(e) => {
          setRaw(e.target.value);
          setEdits(null);
        }}
      />
      {raw.length === 0 ? null : !split ? (
        <p className="text-[12px] text-ink-dim">— 输入需要包含 scheme（如 https://）才能解析 —</p>
      ) : (
        <div className="flex flex-col gap-2">
          <dl className="grid grid-cols-[5em_1fr] gap-y-1 text-[12px]">
            <dt className="text-ink-dim">origin</dt>
            <dd className="font-mono text-ink">{split.origin}</dd>
            <dt className="text-ink-dim">path</dt>
            <dd className="font-mono text-ink">{split.path}</dd>
            {split.hash && (
              <>
                <dt className="text-ink-dim">hash</dt>
                <dd className="font-mono text-ink">{split.hash}</dd>
              </>
            )}
          </dl>

          <table className="w-full text-[12px]">
            <thead className="text-left text-ink-dim">
              <tr>
                <th className="py-1 pr-2 font-normal">键</th>
                <th className="py-1 pr-2 font-normal">值</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={3} className="py-2 text-center text-ink-dim">
                    （无查询参数）
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <tr key={idx} className="border-t border-line">
                    <td className="py-1 pr-2">
                      <input
                        className="input w-full font-mono text-xs"
                        value={row.key}
                        onChange={(e) => setRowAt(idx, { ...row, key: e.target.value })}
                      />
                    </td>
                    <td className="py-1 pr-2">
                      <input
                        className="input w-full font-mono text-xs"
                        value={row.value}
                        onChange={(e) => setRowAt(idx, { ...row, value: e.target.value })}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn-ghost p-1"
                        onClick={() => deleteRowAt(idx)}
                        title="删除该行"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <button type="button" className="btn-ghost self-start text-xs" onClick={addRow}>
            + 新增参数
          </button>

          <div className="rounded-md border border-line bg-bg-soft p-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-wide text-ink-dim">重建后的 URL</span>
            </div>
            <p className="mt-1 break-all font-mono text-xs text-ink">{rebuiltUrl}</p>
          </div>
        </div>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------- */
/* Codec dispatch                                                       */
/* -------------------------------------------------------------------- */

type CodecResult = { ok: true; output: string } | { ok: false; error: string };

function runCodec(
  kind: CodecKind,
  state: PerKindState,
  b64: Base64Opts,
  url: UrlOpts,
  hex: HexOpts,
): CodecResult {
  if (state.input.length === 0) return { ok: true, output: '' };
  if (kind === 'base64') {
    if (state.direction === 'encode') {
      return {
        ok: true,
        output: encodeBase64Text(state.input, {
          variant: b64.variant,
          noPadding: b64.noPadding,
        }),
      };
    }
    const r = decodeBase64(state.input);
    return r.ok ? { ok: true, output: r.text } : { ok: false, error: r.error };
  }
  if (kind === 'url') {
    const r = state.direction === 'encode'
      ? encodeUrl(state.input, url.mode)
      : decodeUrl(state.input, url.mode);
    return r;
  }
  // hex
  if (state.direction === 'encode') {
    return {
      ok: true,
      output: encodeHexText(state.input, {
        uppercase: hex.uppercase,
        separator: SEP_TO_STR[hex.separator],
      }),
    };
  }
  const r = decodeHex(state.input);
  return r.ok ? { ok: true, output: r.text } : { ok: false, error: r.error };
}
